import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./HallmarkPaywall.module.scss"

/**
 * HallmarkPaywall (`/hallmark`) — an original, self-contained EXPIRATION /
 * RENEWAL paywall for Private Photo Vault Premium. Paywall type rolled at
 * random for this run: EXPIRATION.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot message this run was `focus on security and privacy`
 * (the older `default` reset is superseded by it), so it is an ACTIVE STANDING
 * DIRECTIVE at super-high priority. The whole concept is built to it: the pitch
 * is SECURITY & PRIVACY, expressed through the visual language security itself
 * uses to prove authenticity — the security-printed certificate.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "Keep your protection certified." Premium is framed as a CERTIFICATE OF
 * PROTECTION issued to your vault — rendered in the real anti-counterfeit
 * language of secure documents: fine-line GUILLOCHÉ engraving (generated
 * mathematically, not an image), MICROPRINT rules, and a struck HALLMARK SEAL
 * under a holographic FOIL that shifts colour as you tilt / move the pointer
 * (the same tamper-evident hologram you find on a passport or a genuine seal).
 * The certificate carries a "PROTECTED THROUGH" validity date. On an expiration
 * screen that date is lapsing; choosing to renew RE-STRIKES the seal and rolls
 * the date forward a year — a tactile, satisfying "re-certified" moment.
 *
 * ── Divergence (vs. the 80+ prior concepts, incl. the expiration cluster) ───
 * No prior paywall has used the SECURITY-PRINT / guilloché art direction, and
 * none has heroed a holographic assay SEAL. Deliberately different from its
 * expiration neighbours (Deep Freeze frost, Unbroken calendar, Keystone arch,
 * Keep Posted split-flap, Beacon lighthouse, Mainspring music box, Postmark
 * stamp, Current wall-vitrine, Vigil, Brink, Lifeline win-back):
 * Layout      : a single engraved CREDENTIAL / CERTIFICATE with a struck seal
 *               as its centre of gravity — not a hero-over-card, not a meter,
 *               not a countdown board.
 * Interaction : POINTER / DEVICE-TILT PARALLAX drives a live holographic sheen
 *               across the foil + a tap RE-STRIKES the seal. New to the set.
 * Metaphor    : a certificate of authenticity / assay hallmark — protection you
 *               can see is genuine, stamped and dated.
 * Value frame : CERTIFIED / RISK-REVERSAL assurance — "your protection is in
 *               good standing", not fear of loss.
 * Register    : ceremonial, high-assurance, precious-and-official (calm), a
 *               fresh shade away from the cold-urgency of the frost/countdown
 *               expiration screens.
 * CTA frame   : "Renew protection" / re-issue the certificate — renewal, not
 *               first purchase.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * Every certified clause is a REAL Premium protection: the only-you lock
 * (Face ID / Touch ID / PIN), the encrypted Cloud Vault backup, the Decoy
 * Vault, Break-in snapshots, and Recently-Deleted recovery. The screen states
 * plainly that renewing keeps these PROTECTION FEATURES active and NEVER
 * affects the photos already saved — no data-loss-on-expiration threat. No fake
 * countdown, no fabricated scarcity. "Save 76%" is real arithmetic from live
 * product data. Yearly is the featured, default, most prominent price; monthly
 * is a small secondary line. The 7-day free trial is the real trial. The
 * validity dates are honest relative dates (renewal extends a year).
 */

// ── programmatic guilloché ────────────────────────────────────────────────
// A rosette traced as r = R + amp·sin(petals·θ) over one revolution. Two
// interleaved rosettes at different petal counts read as classic security
// engraving — and it's real geometry, not a bundled image (no egress).
function rosette(
  cx: number,
  cy: number,
  R: number,
  amp: number,
  petals: number,
  step = 0.03,
) {
  let d = ""
  for (let t = 0; t <= Math.PI * 2 + step; t += step) {
    const r = R + amp * Math.sin(petals * t)
    const x = cx + r * Math.cos(t)
    const y = cy + r * Math.sin(t)
    d += (t === 0 ? "M" : "L") + x.toFixed(1) + " " + y.toFixed(1) + " "
  }
  return d + "Z"
}

// the vault "specimen" — a few bundled demo tiles sealed inside the window
const SPECIMEN = [
  "/recall/sunset-1.jpg",
  "/recall/beach-2.jpg",
  "/recall/mountain-2.jpg",
  "/recall/dog-1.jpg",
  "/recall/city-1.jpg",
  "/recall/coffee-2.jpg",
]

// the certified clauses — every one a REAL Premium protection
const CLAUSES = [
  {
    key: "lock",
    Icon: svg.featureIcons.PatternLock,
    name: "Only-you lock",
    line: "Face ID · Touch ID · private passcode",
  },
  {
    key: "cloud",
    Icon: svg.featureIcons.CloudVault,
    name: "Encrypted Cloud Vault",
    line: "a private, encrypted backup of your vault",
  },
  {
    key: "decoy",
    Icon: svg.featureIcons.Decoy,
    name: "Decoy Vault",
    line: "a second passcode opens a harmless vault",
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-in snapshots",
    line: "a photo the moment a wrong code is tried",
  },
  {
    key: "trash",
    Icon: svg.featureIcons.Trash,
    name: "Recently-deleted recovery",
    line: "bring a photo back within the recovery window",
  },
]

function monthYear(d: Date) {
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" })
}

const HallmarkPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 76
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  // honest relative validity dates: the current certificate is lapsing now;
  // renewing extends it a full year from today.
  const { lapseDate, renewedDate } = useMemo(() => {
    const now = new Date()
    const renewed = new Date(now)
    renewed.setFullYear(renewed.getFullYear() + 1)
    return { lapseDate: monthYear(now), renewedDate: monthYear(renewed) }
  }, [])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")
  // seal state: "lapsing" (current) → "certified" (previewing a renewal)
  const [certified, setCertified] = useState(false)
  const [struck, setStruck] = useState(0) // increments to retrigger the press
  const rootRef = useRef<HTMLDivElement>(null)

  const guilloche = useMemo(
    () => ({
      outer: rosette(150, 150, 118, 12, 24),
      mid: rosette(150, 150, 96, 16, 15),
      inner: rosette(150, 150, 70, 9, 36),
    }),
    [],
  )

  // ── holographic parallax: write CSS vars straight to the node (no re-render)
  const setTilt = useCallback((nx: number, ny: number) => {
    const el = rootRef.current
    if (!el) return
    el.style.setProperty("--tx", nx.toFixed(3))
    el.style.setProperty("--ty", ny.toFixed(3))
  }, [])

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (reduceMotion) return
      const r = rootRef.current?.getBoundingClientRect()
      if (!r) return
      const nx = ((e.clientX - r.left) / r.width) * 2 - 1
      const ny = ((e.clientY - r.top) / r.height) * 2 - 1
      setTilt(Math.max(-1, Math.min(1, nx)), Math.max(-1, Math.min(1, ny)))
    },
    [reduceMotion, setTilt],
  )

  // device-orientation tilt for touch devices (best-effort, no permission gate)
  useEffect(() => {
    if (reduceMotion) return
    const onOrient = (e: DeviceOrientationEvent) => {
      const gx = (e.gamma ?? 0) / 35 // left/right
      const gy = ((e.beta ?? 0) - 45) / 35 // front/back around a natural hold
      setTilt(Math.max(-1, Math.min(1, gx)), Math.max(-1, Math.min(1, gy)))
    }
    window.addEventListener("deviceorientation", onOrient)
    return () => window.removeEventListener("deviceorientation", onOrient)
  }, [reduceMotion, setTilt])

  // auto-strike on mount: let the seal read as "re-certified" at a glance
  useEffect(() => {
    if (reduceMotion) {
      setCertified(true)
      return
    }
    const t = window.setTimeout(() => {
      setCertified(true)
      setStruck((n) => n + 1)
    }, 1050)
    return () => clearTimeout(t)
  }, [reduceMotion])

  const restrike = useCallback(() => {
    setCertified(true)
    setStruck((n) => n + 1)
  }, [])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  return (
    <div
      ref={rootRef}
      className={`${styles.root} ${certified ? styles.isCertified : ""}`}
      onPointerMove={onPointerMove}
      onPointerLeave={() => setTilt(0, 0)}
    >
      <div className={styles.stageGlow} aria-hidden="true" />

      {/* header — brand clarity in the first glance */}
      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M9 12.2l2.2 2.2L15 10.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.brandName}>
            Private Photo Vault
            <span className={styles.brandTag}>Premium</span>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={() => native.postNativeEvent("close")}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15">
            <path
              d="M6 6l12 12M18 6L6 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* honest expiration status — informational, no fake countdown */}
      <div className={styles.status} role="status">
        <span className={styles.statusDot} aria-hidden="true" />
        Your protection certificate has lapsed — renew to keep it in good
        standing
      </div>

      {/* ── the certificate ──────────────────────────────────────────────── */}
      <section
        className={styles.certificate}
        onClick={restrike}
        aria-label="Certificate of protection"
      >
        {/* engraved guilloché frame + microprint */}
        <span className={styles.microprint} aria-hidden="true" />
        <span className={styles.frame} aria-hidden="true" />

        <p className={styles.certKicker}>Private Photo Vault</p>
        <h1 className={styles.certTitle}>Certificate of Protection</h1>
        <p className={styles.certSerial}>
          No. PPV·04-{String(2600 + (struck % 400)).padStart(4, "0")} — issued
          to this device
        </p>

        {/* the struck hallmark seal under holographic foil */}
        <div className={styles.sealWrap}>
          <svg
            key={struck}
            className={styles.seal}
            viewBox="0 0 300 300"
            width="220"
            height="220"
            aria-hidden="true"
          >
            <defs>
              <radialGradient id="hm-metal" cx="42%" cy="36%" r="70%">
                <stop offset="0%" stopColor="#fff4d6" />
                <stop offset="38%" stopColor="#e9b96b" />
                <stop offset="72%" stopColor="#b6832f" />
                <stop offset="100%" stopColor="#7c5312" />
              </radialGradient>
              <clipPath id="hm-clip">
                <circle cx="150" cy="150" r="132" />
              </clipPath>
            </defs>

            {/* struck disc */}
            <circle
              cx="150"
              cy="150"
              r="132"
              fill="url(#hm-metal)"
              className={styles.sealDisc}
            />
            <circle
              cx="150"
              cy="150"
              r="132"
              fill="none"
              stroke="#5a3d0e"
              strokeWidth="2"
              opacity="0.55"
            />

            {/* engraved guilloché */}
            <g
              clipPath="url(#hm-clip)"
              stroke="#4b3208"
              fill="none"
              opacity="0.42"
            >
              <path d={guilloche.outer} strokeWidth="0.8" />
              <path d={guilloche.mid} strokeWidth="0.8" />
              <path d={guilloche.inner} strokeWidth="0.8" />
            </g>

            {/* rings + engraved legend */}
            <circle
              cx="150"
              cy="150"
              r="118"
              fill="none"
              stroke="#5a3d0e"
              strokeWidth="1.4"
              opacity="0.5"
            />
            <circle
              cx="150"
              cy="150"
              r="58"
              fill="none"
              stroke="#5a3d0e"
              strokeWidth="1.2"
              opacity="0.45"
            />
            <path id="hm-top" d="M54 150 A96 96 0 0 1 246 150" fill="none" />
            <path id="hm-bot" d="M238 150 A88 88 0 0 1 62 150" fill="none" />
            <text
              className={styles.sealLegend}
              textAnchor="middle"
              dominantBaseline="middle"
            >
              <textPath href="#hm-top" startOffset="50%">
                CERTIFIED · PROTECTION
              </textPath>
            </text>
            <text
              className={styles.sealLegendSm}
              textAnchor="middle"
              dominantBaseline="middle"
            >
              <textPath href="#hm-bot" startOffset="50%">
                PRIVATE PHOTO VAULT
              </textPath>
            </text>

            {/* the vault shield mark, engraved */}
            <g
              transform="translate(150 150)"
              stroke="#4b3208"
              strokeWidth="6"
              fill="none"
              strokeLinejoin="round"
              strokeLinecap="round"
              opacity="0.85"
            >
              <path d="M0 -42 L34 -28 V6 C34 30 19 48 0 56 C-19 48 -34 30 -34 6 V-28 Z" />
              <path d="M-15 4 L-4 15 L17 -10" strokeWidth="7" />
            </g>

            {/* soft glare that tracks tilt, clipped to the disc */}
            <g clipPath="url(#hm-clip)">
              <ellipse
                cx="118"
                cy="96"
                rx="78"
                ry="52"
                className={styles.glare}
              />
            </g>
          </svg>

          {/* the ring pulse on strike */}
          <span
            key={`p${struck}`}
            className={styles.strikePulse}
            aria-hidden="true"
          />
        </div>

        {/* validity — lapsing vs. re-certified */}
        <div className={styles.validity}>
          <span className={styles.validLabel}>Protected through</span>
          <span className={styles.validValue}>
            <span className={styles.validOld}>{lapseDate}</span>
            <span className={styles.validArrow} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path
                  d="M5 12h13M13 6l6 6-6 6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className={styles.validNew}>{renewedDate}</span>
          </span>
          <span className={styles.validFoot}>on renewal · +12 months</span>
        </div>

        {/* the sealed specimen window — the vault this certifies */}
        <div className={styles.specimen} aria-hidden="true">
          <span className={styles.specimenLabel}>Specimen · demo vault</span>
          <div className={styles.specimenGrid}>
            {SPECIMEN.map((src, i) => (
              <span
                key={src}
                className={styles.tile}
                style={{ ["--i" as any]: i }}
              >
                <img src={src} alt="" draggable={false} />
              </span>
            ))}
            <span className={styles.specimenSheen} />
          </div>
        </div>

        {/* certified clauses — real Premium protections */}
        <ul className={styles.clauses}>
          <li className={styles.clausesHead}>
            This certifies your vault is protected by
          </li>
          {CLAUSES.map((c) => {
            const Icon = c.Icon
            return (
              <li key={c.key} className={styles.clause}>
                <span className={styles.clauseIcon} aria-hidden="true">
                  <Icon />
                </span>
                <span className={styles.clauseText}>
                  <span className={styles.clauseName}>{c.name}</span>
                  <span className={styles.clauseLine}>{c.line}</span>
                </span>
                <span className={styles.clauseSeal} aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="13" height="13">
                    <path
                      d="M5 12.5l4.2 4.2L19 7"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </li>
            )
          })}
        </ul>
      </section>

      {/* honest, compliant reassurance */}
      <p className={styles.assure}>
        <span className={styles.assureMark} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="13" height="13">
            <path
              d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        Renewing keeps these protection features active. It never affects the
        photos you&rsquo;ve already saved — your photos are always yours to
        keep.
      </p>

      {/* ── PRICING: yearly featured, monthly small & secondary ───────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.yearly} ${
            plan === "yearly" ? styles.selected : ""
          }`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyGlow} aria-hidden="true" />
          <span className={styles.yearlyHead}>
            <span className={styles.yearlyName}>
              Yearly — certified 12 months
            </span>
            {savingsPct > 0 && (
              <span className={styles.saveTag}>Save {savingsPct}%</span>
            )}
          </span>
          <span className={styles.yearlyPriceRow}>
            <span className={styles.yearlyPrice}>{yearlyPrice}</span>
            <span className={styles.yearlyUnit}>/ year</span>
            <span className={styles.check} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M5 12.5l4.2 4.2L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
          <span className={styles.yearlyPerMo}>
            7 days free, then about {yearlyPerMonth}/mo billed yearly
          </span>
        </button>

        <button
          className={`${styles.monthly} ${
            plan === "monthly" ? styles.monthlySel : ""
          }`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthlyName}>Month-to-month</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <div className={styles.footerLinks}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>

      <p className={styles.credit}>Specimen library from Unsplash</p>

      <div className={styles.dock}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly"
              ? "Renew protection — start 7 days free"
              : "Renew monthly"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `7 days free, then ${yearlyPrice}/yr · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
        </button>
      </div>
    </div>
  )
}

export default HallmarkPaywall
